import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useConnectionsStore, useCampaignsStore } from '@/store';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Wifi, Calendar, Users, Send, TrendingUp, Zap, Sparkles, ChevronDown, AlertCircle, XCircle, Filter, Wallet, Coins, Loader2, RefreshCw, BrainCircuit, Bell, ArrowRight, DollarSign, Clock, Smartphone } from 'lucide-react';
import { api } from '@/store';
import FinancialDashboard from '@/components/FinancialDashboard';
import Announcements from '@/components/Announcements';
import BaseHealthReport from '@/components/BaseHealthReport';


// ============= NEXU IA ANALYSIS CARD =============
function NexuIACard({ stats }) {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    checkToday();
  }, []);

  const checkToday = async () => {
    try {
      const r = await api.get('/ai-analysis/today');
      setAvailable(r.data.available);
      if (r.data.analysis) setAnalysis(r.data.analysis);
    } catch {} finally { setChecked(true); }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post('/ai-analysis');
      setAnalysis(r.data);
      setAvailable(false);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  if (!checked) return null;

  return (
    <Card className="border-primary/20 overflow-hidden">
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center">
              <BrainCircuit className="w-4 h-4 text-primary" />
            </div>
            <span className="font-bold text-sm text-foreground">Analise Nexu IA</span>
          </div>
          <span className="text-[10px] text-primary/60 font-mono bg-primary/5 px-2 py-0.5 rounded-full border border-primary/10">
            1 gratis/dia
          </span>
        </div>

        {/* Content */}
        <div className="px-5 pb-4">
          {analysis ? (
            <>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{analysis.text}</p>
              
              {/* Action buttons */}
              {analysis.data && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {analysis.data.expiring_7d > 0 && (
                    <button onClick={() => navigate('/app-config')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/5 border border-white/10 rounded-lg hover:border-primary/30 hover:bg-primary/5 transition-colors">
                      <Bell className="w-3 h-3" />
                      Disparar lembrete · {analysis.data.expiring_7d}
                    </button>
                  )}
                  {analysis.data.expired > 0 && (
                    <button onClick={() => navigate('/customers')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/5 border border-white/10 rounded-lg hover:border-primary/30 hover:bg-primary/5 transition-colors">
                      <ArrowRight className="w-3 h-3" />
                      Renovar vencidos · {analysis.data.expired}
                    </button>
                  )}
                  <button onClick={() => navigate('/financial')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/5 border border-white/10 rounded-lg hover:border-primary/30 hover:bg-primary/5 transition-colors">
                    <TrendingUp className="w-3 h-3" />
                    Ver Financeiro
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-3">Gere uma analise inteligente do seu negocio com base nos dados dos ultimos 30 dias</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-white/5 bg-white/[0.01]">
          {analysis ? (
            <p className="text-[10px] text-muted-foreground/50">
              Voce usou sua analise gratuita de hoje · proxima disponivel amanha
            </p>
          ) : (
            <p className="text-[10px] text-muted-foreground/50">
              Analise gratuita disponivel
            </p>
          )}
          <button
            onClick={generate}
            disabled={loading || !available}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {loading ? 'Analisando...' : 'Atualizar'}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}


// Elegant Particle Animation - Clean and Professional
function ParticleBackground() {
  const canvasRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    if (!isReady) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    let animationId;
    let particles = [];
    let mouseX = -1000;
    let mouseY = -1000;
    let isRunning = true;
    
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      createParticles(rect.width, rect.height);
    };
    
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    };
    
    const handleMouseLeave = () => {
      mouseX = -1000;
      mouseY = -1000;
    };
    
    const createParticles = (width, height) => {
      particles = [];
      const count = 50;
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 2.5 + 1,
          opacity: Math.random() * 0.5 + 0.2,
          baseOpacity: Math.random() * 0.5 + 0.2,
          pulseSpeed: Math.random() * 0.03 + 0.01,
          pulseOffset: Math.random() * Math.PI * 2,
        });
      }
    };
    
    let time = 0;
    
    const animate = () => {
      if (!isRunning) return;
      
      const rect = canvas.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      
      time += 0.016;
      ctx.clearRect(0, 0, width, height);
      
      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 100) {
            const lineOpacity = (1 - dist / 100) * 0.12;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(34, 197, 94, ${lineOpacity})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      
      // Update and draw particles
      particles.forEach((p) => {
        // Mouse repulsion
        const dx = mouseX - p.x;
        const dy = mouseY - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < 100 && dist > 0) {
          const force = (100 - dist) / 100 * 0.5;
          p.vx -= (dx / dist) * force * 0.05;
          p.vy -= (dy / dist) * force * 0.05;
        }
        
        // Damping and drift
        p.vx *= 0.99;
        p.vy *= 0.99;
        p.vx += (Math.random() - 0.5) * 0.02;
        p.vy += (Math.random() - 0.5) * 0.02;
        
        p.x += p.vx;
        p.y += p.vy;
        
        // Wrap edges
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        if (p.y < -10) p.y = height + 10;
        if (p.y > height + 10) p.y = -10;
        
        // Pulse opacity
        p.opacity = p.baseOpacity + Math.sin(time * p.pulseSpeed * 60 + p.pulseOffset) * 0.15;
        
        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(34, 197, 94, ${p.opacity})`;
        ctx.fill();
      });
      
      animationId = requestAnimationFrame(animate);
    };
    
    resize();
    window.addEventListener('resize', resize);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    animate();
    
    return () => {
      isRunning = false;
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isReady]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
      style={{ background: 'transparent', pointerEvents: 'auto' }}
    />
  );
}

// Mini Chart Component
function MiniChart({ data, color = '#22c55e' }) {
  const maxValue = Math.max(...data, 1);
  const height = 60;
  
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((value, index) => (
        <div
          key={index}
          className="flex-1 rounded-t transition-all duration-300 hover:opacity-80"
          style={{
            height: `${(value / maxValue) * height}px`,
            background: `linear-gradient(to top, ${color}40, ${color})`,
            minHeight: value > 0 ? '4px' : '2px',
          }}
          title={`${value} envios`}
        />
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { connections, fetchConnections } = useConnectionsStore();
  const { campaigns, fetchCampaigns } = useCampaignsStore();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7');
  const [ownerFilter, setOwnerFilter] = useState('mine');
  const [pushEnabled, setPushEnabled] = useState(true);
  const [pushLoading, setPushLoading] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isSimple = user?.role === 'simple';
  const isVendedor = user?.role === 'vendedor';

  // Expõe os números da dashboard para o app nativo (modo offline em cache)
  useEffect(() => {
    try {
      const msDay = 1000 * 60 * 60 * 24;
      const exp = (user?.expires_at || stats?.expires_at) ? new Date(user?.expires_at || stats?.expires_at) : null;
      const accountDaysLeft = exp ? Math.ceil((exp.getTime() - Date.now()) / msDay) : null;
      const total = stats?.customers_count ?? null;
      const active = stats?.customers_active ?? null;
      const expired = stats?.customers_expired ?? null;
      const expiring = (total != null && active != null && expired != null)
        ? Math.max(0, total - active - expired) : null;
      window.__nexuDashboard = {
        total, active, expiring, expired,
        accountDaysLeft: (accountDaysLeft != null && accountDaysLeft >= 0) ? accountDaysLeft : (accountDaysLeft != null ? accountDaysLeft : null),
        accountExpiration: exp ? exp.toLocaleDateString('pt-BR') : null,
      };
    } catch (e) { /* silencioso */ }
  }, [stats, user]);

  const fetchStats = useCallback(async () => {
    try {
      const ownerParam = isAdmin ? `&owner_filter=${ownerFilter}` : '';
      const response = await api.get(`/stats/dashboard?days=${period}${ownerParam}`);
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  }, [period, ownerFilter, isAdmin]);

  useEffect(() => {
    // Fetch connections and campaigns with owner filter for admin
    if (isAdmin) {
      fetchConnections(false, ownerFilter);
      fetchCampaigns(ownerFilter);
    } else {
      fetchConnections();
      fetchCampaigns();
    }
    fetchStats();
  }, [fetchConnections, fetchCampaigns, fetchStats, isAdmin, ownerFilter]);

  // Check push notification status
  useEffect(() => {
    const checkPush = async () => {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) { setPushEnabled(false); return; }
      if (Notification.permission === 'granted') {
        try {
          const r = await api.get('/panel-push/status');
          setPushEnabled(r.data.enabled);
        } catch { setPushEnabled(false); }
      } else {
        setPushEnabled(false);
      }
    };
    checkPush();
  }, []);

  const enablePush = async () => {
    setPushLoading(true);
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        alert('Seu navegador nao suporta notificacoes push'); return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { alert('Permissao negada'); return; }
      
      // Registrar service worker do painel
      const reg = await navigator.serviceWorker.register('/sw-panel.js');
      await navigator.serviceWorker.ready;
      
      // Buscar VAPID key
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const vapidRes = await fetch(`${API_URL}/api/web-push/vapid-key`);
      const { public_key } = await vapidRes.json();
      
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
      };
      
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(public_key)
        });
      }
      
      // Enviar subscription para o backend
      const token = localStorage.getItem('nexus-token');
      await fetch(`${API_URL}/api/panel-push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscription: sub.toJSON() })
      });
      
      setPushEnabled(true);
    } catch (e) {
      console.error('Erro ao ativar push:', e);
      alert('Erro ao ativar notificacoes');
    } finally { setPushLoading(false); }
  };

  const connectedCount = connections.filter(c => c.status === 'connected').length;
  const activeCampaigns = campaigns.filter(c => ['active', 'running'].includes(c.status)).length;

  // Dashboard dedicada pro Vendedor
  if (isVendedor) {
    return (
      <div data-testid="dashboard-page" className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Olá, {user?.username}</h1>
            <p className="text-sm text-muted-foreground">Painel do Vendedor</p>
          </div>
          <Badge variant="outline" className="text-xs bg-indigo-500/15 text-indigo-400 border-indigo-500/20">
            VENDEDOR
          </Badge>
        </div>

        {/* KPIs */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Saldo NexuPay</p>
                <p className="text-2xl font-bold text-emerald-500">R$ {(stats?.nexupay_balance || 0).toFixed(2)}</p>
              </div>
              <Wallet className="w-8 h-8 text-emerald-500 opacity-40" />
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Comissões do Mês</p>
                <p className="text-2xl font-bold text-blue-500">R$ {(stats?.commissions_month || 0).toFixed(2)}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{stats?.commissions_count || 0} transação(ões)</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-500 opacity-40" />
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Clientes Ativos</p>
                <p className="text-2xl font-bold text-primary">{stats?.customers_active || 0}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">de {stats?.customers_count || 0} total</p>
              </div>
              <Users className="w-8 h-8 text-primary opacity-40" />
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Vencidos</p>
                <p className="text-2xl font-bold text-red-400">{stats?.customers_expired || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-red-400 opacity-40" />
            </div>
          </Card>
        </div>

        {/* Comissão config */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">Sua Comissão</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Tipo</p>
              <p className="text-sm font-medium">{stats?.commission_type === 'percent' ? 'Porcentagem' : 'Valor Fixo'}</p>
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Valor</p>
              <p className="text-sm font-medium">{stats?.commission_type === 'percent' ? `${stats?.commission_value || 0}%` : `R$ ${(stats?.commission_value || 0).toFixed(2)}`}</p>
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Status</p>
              <Badge className={stats?.commission_enabled ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : 'bg-red-500/15 text-red-400 border-red-500/20'}>
                {stats?.commission_enabled ? 'Ativa' : 'Desativada'}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Ações rápidas */}
        <div className="grid gap-3 md:grid-cols-3">
          <button onClick={() => navigate('/customers')} className="p-4 rounded-xl border border-border/50 hover:bg-muted/50 transition-colors text-left">
            <Users className="w-5 h-5 text-primary mb-2" />
            <p className="text-sm font-medium">Gestor de Clientes</p>
            <p className="text-xs text-muted-foreground">Cadastrar, editar, renovar</p>
          </button>
          <button onClick={() => navigate('/nexupay')} className="p-4 rounded-xl border border-border/50 hover:bg-muted/50 transition-colors text-left">
            <Wallet className="w-5 h-5 text-emerald-500 mb-2" />
            <p className="text-sm font-medium">NexuPay</p>
            <p className="text-xs text-muted-foreground">Saldo, saques, extrato</p>
          </button>
          <button onClick={() => navigate('/app-config')} className="p-4 rounded-xl border border-border/50 hover:bg-muted/50 transition-colors text-left">
            <Smartphone className="w-5 h-5 text-blue-500 mb-2" />
            <p className="text-sm font-medium">App Config</p>
            <p className="text-xs text-muted-foreground">Configurações do aplicativo</p>
          </button>
        </div>
      </div>
    );
  }

  // Dashboard dedicada pro Nexu Simple
  if (isSimple) {
    const expiresAt = user?.expires_at || stats?.expires_at || '';
    let daysLeft = 0;
    let expFormatted = '';
    try {
      const exp = new Date(expiresAt);
      daysLeft = Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24));
      expFormatted = exp.toLocaleDateString('pt-BR');
    } catch {}

    return (
      <div data-testid="dashboard-page" className="space-y-6 animate-fade-in">
        {/* Push Banner */}
        {!pushEnabled && (
          <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500 shrink-0" />
              <p className="text-xs text-yellow-500">Ative as notificacoes para receber alertas de pagamentos mesmo com o site fechado!</p>
            </div>
            <button onClick={enablePush} disabled={pushLoading} className="shrink-0 px-3 py-1.5 text-xs font-medium bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50" data-testid="enable-push-btn">
              {pushLoading ? 'Ativando...' : 'Ativar'}
            </button>
          </div>
        )}

        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-primary" />
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground">Painel</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            Ola, <span className="text-primary font-semibold">{user?.username}</span>!
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="border-primary/20">
            <CardContent className="p-4">
              <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center mb-2">
                <Users className="w-4 h-4 text-primary" />
              </div>
              <p className="font-bold text-2xl text-foreground">{stats?.customers_count || 0}</p>
              <p className="text-xs text-muted-foreground">Total de Clientes</p>
            </CardContent>
          </Card>

          <Card className="border-emerald-500/20">
            <CardContent className="p-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center mb-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="font-bold text-2xl text-emerald-500">{stats?.customers_active || 0}</p>
              <p className="text-xs text-muted-foreground">Clientes Ativos</p>
            </CardContent>
          </Card>

          <Card className="border-red-500/20">
            <CardContent className="p-4">
              <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center mb-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
              </div>
              <p className="font-bold text-2xl text-red-500">{stats?.customers_expired || 0}</p>
              <p className="text-xs text-muted-foreground">Clientes Expirados</p>
            </CardContent>
          </Card>

          <Card className="border-purple-500/20">
            <CardContent className="p-4">
              <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center mb-2">
                <Users className="w-4 h-4 text-purple-500" />
              </div>
              <p className="font-bold text-2xl text-purple-500">{stats?.resellers_count || 0}</p>
              <p className="text-xs text-muted-foreground">Revendedores</p>
            </CardContent>
          </Card>
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Creditos */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-muted-foreground">Creditos Disponiveis</span>
                <Coins className="w-4 h-4 text-yellow-500" />
              </div>
              <p className="font-bold text-3xl text-yellow-500">{stats?.credits ?? user?.credits ?? 0}</p>
              <button onClick={() => navigate('/credit-shop')} className="text-[10px] text-primary hover:underline mt-2 block">Comprar mais</button>
            </CardContent>
          </Card>

          {/* Receita */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-muted-foreground">Receita ({period}d)</span>
                <TrendingUp className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="font-bold text-3xl text-emerald-500">R$ {(stats?.revenue_period || 0).toFixed(2)}</p>
              <button onClick={() => navigate('/financial')} className="text-[10px] text-primary hover:underline mt-2 block">Ver transacoes</button>
            </CardContent>
          </Card>

          {/* NexuPay */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-muted-foreground">Saldo NexuPay</span>
                <Wallet className="w-4 h-4 text-emerald-500" />
              </div>
              {stats?.nexupay_balance !== null && stats?.nexupay_balance !== undefined ? (
                <>
                  <p className="font-bold text-3xl text-emerald-500">R$ {(stats.nexupay_balance).toFixed(2)}</p>
                  <button onClick={() => navigate('/nexupay')} className="text-[10px] text-primary hover:underline mt-2 block">Gerenciar carteira</button>
                </>
              ) : (
                <>
                  <p className="font-bold text-lg text-muted-foreground">Desativado</p>
                  <button onClick={() => navigate('/gateways')} className="text-[10px] text-primary hover:underline mt-2 block">Ativar NexuPay</button>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Validade do Painel */}
        <Card className={daysLeft <= 7 ? 'border-red-500/30' : 'border-border'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Validade do Painel</p>
                <p className="font-bold text-lg text-foreground">{expFormatted || 'N/A'}</p>
                {daysLeft > 0 ? (
                  <p className={`text-xs mt-0.5 ${daysLeft <= 7 ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>
                    {daysLeft} dia{daysLeft !== 1 ? 's' : ''} restante{daysLeft !== 1 ? 's' : ''}
                  </p>
                ) : (
                  <p className="text-xs text-red-500 font-semibold mt-0.5">Expirado!</p>
                )}
              </div>
              <button onClick={() => navigate('/my-plans')} className="px-4 py-2 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                Renovar
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Nexu IA Analysis */}
        <NexuIACard stats={stats} />

        {/* Acoes Rapidas */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-heading font-semibold text-foreground mb-4">Acoes Rapidas</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 border-border hover:border-primary hover:bg-primary/5" onClick={() => navigate('/resellers')}>
                <Users className="w-5 h-5 text-primary" />
                <span className="text-xs">Revendedores</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 border-border hover:border-primary hover:bg-primary/5" onClick={() => navigate('/credit-shop')}>
                <Coins className="w-5 h-5 text-yellow-500" />
                <span className="text-xs">Comprar Creditos</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 border-border hover:border-primary hover:bg-primary/5" onClick={() => navigate('/nexupay')}>
                <Wallet className="w-5 h-5 text-emerald-500" />
                <span className="text-xs">NexuPay</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 border-border hover:border-primary hover:bg-primary/5" onClick={() => navigate('/financial')}>
                <TrendingUp className="w-5 h-5 text-green-500" />
                <span className="text-xs">Financeiro</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard padrão (admin/master/reseller)

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-fade-in">
      {/* Avisos / novidades */}
      <Announcements />

      {/* Push Notification Banner */}
      {!pushEnabled && (
        <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-500 shrink-0" />
            <p className="text-xs text-yellow-500">Ative as notificacoes para receber alertas de pagamentos mesmo com o site fechado!</p>
          </div>
          <button
            onClick={enablePush}
            disabled={pushLoading}
            className="shrink-0 px-3 py-1.5 text-xs font-medium bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50"
            data-testid="enable-push-btn"
          >
            {pushLoading ? 'Ativando...' : 'Ativar'}
          </button>
        </div>
      )}

      {/* Hero Section with Particle Background */}
      <Card className="relative overflow-hidden border-primary/10 bg-gradient-to-br from-card via-card to-primary/5">
        {/* Particle Animation Layer */}
        <div className="absolute inset-0 z-0">
          <ParticleBackground />
        </div>
        
        {/* Content Layer */}
        <CardContent className="relative z-10 p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-5 h-5 text-primary" />
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground">Dashboard</h1>
              </div>
              <p className="text-muted-foreground text-sm">
                Olá, <span className="text-primary font-semibold">{user?.username}</span>! Aqui está seu resumo.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Admin Owner Filter */}
              {isAdmin && (
                <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                  <SelectTrigger className="w-40 bg-background/80 backdrop-blur-sm border-border text-foreground">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mine">Apenas Meus</SelectItem>
                    <SelectItem value="all">Todo Sistema</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32 bg-background/80 backdrop-blur-sm border-border text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 dias</SelectItem>
                  <SelectItem value="30">30 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Dispositivos */}
            <div className="bg-background/70 backdrop-blur-sm rounded-xl p-4 border border-border/30 transition-all hover:border-primary/30 hover:bg-background/90">
              <div className="flex items-center justify-between mb-2">
                <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
                  <Wifi className="w-4 h-4 text-primary" />
                </div>
              </div>
              <p className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                {connectedCount}<span className="text-base sm:text-lg text-muted-foreground">/{connections.length}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Dispositivos</p>
            </div>

            {/* Campanhas Ativas */}
            <div className="bg-background/70 backdrop-blur-sm rounded-xl p-4 border border-border/30 transition-all hover:border-blue-500/30 hover:bg-background/90">
              <div className="flex items-center justify-between mb-2">
                <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-blue-500" />
                </div>
              </div>
              <p className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                {activeCampaigns}<span className="text-base sm:text-lg text-muted-foreground">/{campaigns.length}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Campanhas</p>
            </div>

            {/* Revendedores / Clientes / Envios */}
            {(user?.role === 'admin' || user?.role === 'master') ? (
              <div className="bg-background/70 backdrop-blur-sm rounded-xl p-4 border border-border/30 transition-all hover:border-purple-500/30 hover:bg-background/90">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center">
                    <Users className="w-4 h-4 text-purple-500" />
                  </div>
                </div>
                <p className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                  {stats?.resellers_count || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Revendedores</p>
              </div>
            ) : isSimple ? (
              <div className="bg-background/70 backdrop-blur-sm rounded-xl p-4 border border-border/30 transition-all hover:border-purple-500/30 hover:bg-background/90">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center">
                    <Users className="w-4 h-4 text-purple-500" />
                  </div>
                </div>
                <p className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                  {stats?.customers_count || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Clientes</p>
              </div>
            ) : (
              <div className="bg-background/70 backdrop-blur-sm rounded-xl p-4 border border-border/30 transition-all hover:border-yellow-500/30 hover:bg-background/90">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 rounded-lg bg-yellow-500/15 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-yellow-500" />
                  </div>
                </div>
                <p className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                  {stats?.sends_period || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Envios ({period}d)</p>
              </div>
            )}

            {/* Envios Hoje */}
            <div className="bg-background/70 backdrop-blur-sm rounded-xl p-4 border border-border/30 transition-all hover:border-green-500/30 hover:bg-background/90">
              <div className="flex items-center justify-between mb-2">
                <div className="w-9 h-9 rounded-lg bg-green-500/15 flex items-center justify-center">
                  <Send className="w-4 h-4 text-green-500" />
                </div>
              </div>
              <p className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                {stats?.sends_today || 0}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Envios Hoje</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                <span className="text-sm font-medium text-foreground">Envios ({period} dias)</span>
              </div>
              <span className="text-2xl font-bold text-primary">{stats?.sends_period || 0}</span>
            </div>
            <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-yellow-500 to-green-500 rounded-full transition-all"
                style={{ width: `${Math.min(100, (stats?.sends_period || 0) / 10)}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-4">
            <Collapsible>
              <CollapsibleTrigger asChild>
                <div className="cursor-pointer">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-foreground">Taxa de Sucesso</span>
                      {stats?.total_failed > 0 && (
                        <span className="flex items-center gap-1 text-xs text-destructive">
                          <AlertCircle className="w-3 h-3" />
                          {stats.total_failed} erro{stats.total_failed > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-green-500">{stats?.success_rate || 100}%</span>
                      {stats?.recent_errors?.length > 0 && (
                        <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform" />
                      )}
                    </div>
                  </div>
                  <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all"
                      style={{ width: `${stats?.success_rate || 100}%` }}
                    />
                  </div>
                </div>
              </CollapsibleTrigger>
              
              {stats?.recent_errors?.length > 0 && (
                <CollapsibleContent>
                  <div className="mt-4 pt-4 border-t border-border/50 space-y-2">
                    <p className="text-xs text-muted-foreground font-medium mb-2">Últimos erros:</p>
                    {stats.recent_errors.map((error, idx) => (
                      <div 
                        key={error.id || idx} 
                        className="flex items-start gap-2 p-2 rounded-lg bg-destructive/5 border border-destructive/10"
                      >
                        <XCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium text-foreground truncate">
                              {error.group_name}
                            </span>
                            <span className="text-[10px] text-muted-foreground flex-shrink-0">
                              {new Date(error.sent_at).toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <p className="text-[10px] text-destructive truncate mt-0.5">
                            {error.error}
                          </p>
                          {error.campaign_name && (
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              Campanha: {error.campaign_name}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              )}
            </Collapsible>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-foreground">Concluídas</span>
              </div>
              <span className="text-2xl font-bold text-blue-500">
                {campaigns.filter(c => c.status === 'completed').length}
              </span>
            </div>
            <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all"
                style={{ 
                  width: campaigns.length > 0 
                    ? `${(campaigns.filter(c => c.status === 'completed').length / campaigns.length) * 100}%` 
                    : '0%' 
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card className="glass-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-foreground">Envios por Dia</h3>
            <span className="text-xs text-muted-foreground">Últimos {period} dias</span>
          </div>
          
          {loading ? (
            <div className="h-20 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <MiniChart data={stats?.daily_sends || Array(parseInt(period)).fill(0)} />
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>{period === '7' ? '7 dias atrás' : '30 dias atrás'}</span>
                <span>Hoje</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dashboard financeiro */}
      {!isSimple && <FinancialDashboard api={api} />}

      {/* Saúde da base */}
      <BaseHealthReport api={api} />

      {/* Nexu IA Analysis */}
      <NexuIACard stats={stats} />

      {/* Quick Actions - hidden for Nexu Simple */}
      {!isSimple && (
        <Card className="glass-card">
          <CardContent className="p-4">
            <h3 className="font-heading font-semibold text-foreground mb-4">Ações Rápidas</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col gap-2 border-border hover:border-primary hover:bg-primary/5"
                onClick={() => navigate('/connections')}
              >
                <Wifi className="w-5 h-5 text-primary" />
                <span className="text-xs">Nova Conexão</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col gap-2 border-border hover:border-primary hover:bg-primary/5"
                onClick={() => navigate('/campaigns/new')}
              >
                <Calendar className="w-5 h-5 text-primary" />
                <span className="text-xs">Nova Campanha</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col gap-2 border-border hover:border-primary hover:bg-primary/5"
                onClick={() => navigate('/templates')}
              >
                <Send className="w-5 h-5 text-primary" />
                <span className="text-xs">Templates</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col gap-2 border-border hover:border-primary hover:bg-primary/5"
                onClick={() => navigate('/history')}
              >
                <TrendingUp className="w-5 h-5 text-primary" />
                <span className="text-xs">Histórico</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
